import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Badge, Table } from "react-bootstrap";
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign } from "react-icons/fi";
import { toast } from "react-toastify";
import api from "../services/api";

const fmt = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(n);

const emptyForm = {
  name: "",
  use_override: false,
  override_price: "",
  is_active: true,
};

const PaymentMethods = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/payment-methods");
      setMethods(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name,
      use_override: m.override_price != null,
      override_price: m.override_price ?? "",
      is_active: Boolean(m.is_active),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    if (
      form.use_override &&
      (form.override_price === "" ||
        isNaN(Number(form.override_price)) ||
        Number(form.override_price) < 0)
    ) {
      toast.error("Harga override harus angka >= 0");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        override_price: form.use_override ? Number(form.override_price) : null,
        is_active: form.is_active,
      };
      if (editId) {
        await api.put(`/payment-methods/${editId}`, payload);
        toast.success("Berhasil diperbarui");
      } else {
        await api.post("/payment-methods", payload);
        toast.success("Berhasil ditambahkan");
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan");
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/payment-methods/${deleteId}`);
      toast.success("Berhasil dihapus");
      setShowDeleteModal(false);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Metode Pembayaran Khusus</h4>
          <small className="text-muted">
            Metode yang aktif akan muncul di kasir POS. Jika override harga
            diaktifkan, harga per item layanan akan diganti sesuai nilai yang
            ditentukan.
          </small>
        </div>
        <Button variant="primary" onClick={openAdd}>
          <FiPlus className="me-1" /> Tambah
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="card shadow-sm">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Nama Metode Pembayaran</th>
                <th>Harga Override</th>
                <th>Status</th>
                <th className="text-end">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {methods.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    Belum ada metode pembayaran khusus
                  </td>
                </tr>
              ) : (
                methods.map((m, i) => (
                  <tr key={m.id}>
                    <td>{i + 1}</td>
                    <td className="fw-semibold">
                      <FiDollarSign className="me-1 text-primary" />
                      {m.name}
                    </td>
                    <td>
                      {m.override_price != null ? (
                        fmt(m.override_price) + " / layanan"
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {m.is_active ? (
                        <Badge bg="success">Aktif</Badge>
                      ) : (
                        <Badge bg="secondary">Nonaktif</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => openEdit(m)}
                      >
                        <FiEdit2 />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => confirmDelete(m.id)}
                      >
                        <FiTrash2 />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editId ? "Edit" : "Tambah"} Metode Pembayaran Khusus
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Nama Metode Pembayaran
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Contoh: Member Promo, Company Rate, ..."
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              label="Override harga layanan"
              checked={form.use_override}
              onChange={(e) =>
                setForm({ ...form, use_override: e.target.checked })
              }
            />
            <Form.Text className="text-muted">
              Aktifkan jika metode ini mengganti harga per item layanan.
            </Form.Text>
          </Form.Group>
          {form.use_override && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Override Harga (Rp)
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                placeholder="Harga per item layanan"
                value={form.override_price}
                onChange={(e) =>
                  setForm({ ...form, override_price: e.target.value })
                }
              />
              <Form.Text className="text-muted">
                Total = override × jumlah item layanan (produk tetap harga
                normal).
              </Form.Text>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Check
              type="switch"
              label="Aktif (tampil di kasir POS)"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Hapus Metode Pembayaran</Modal.Title>
        </Modal.Header>
        <Modal.Body>Yakin ingin menghapus metode pembayaran ini?</Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentMethods;
